import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  LoadingState,
} from '@cca/ui';
import { apiRequest } from '../lib/api.js';
import { ShopifyConnection } from '../components/shopify-connection.js';
import { RequireSession } from '../components/require-session.js';
import { useRuntimeConfig } from '../lib/runtime-config.js';
import { rootRoute } from './root.js';

interface HealthResponse {
  readonly status: string;
  readonly appEnv: string;
  readonly simulatorEnabled: boolean;
  readonly correlationId: string;
}

function SettingsPage() {
  const config = useRuntimeConfig();
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => apiRequest<HealthResponse>('/health'),
    refetchInterval: 30_000,
  });

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Settings</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Environment and connection status.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Product</CardTitle>
          <CardDescription>
            The product name is configuration. Change <code>PUBLIC_PRODUCT_NAME</code> to rebrand
            without a code change.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p>
            <span className="text-slate-500 dark:text-slate-400">Name: </span>
            {config.product.name}
          </p>
          <p>
            <span className="text-slate-500 dark:text-slate-400">Agent: </span>
            {config.product.agentName}
          </p>
          <p>
            <span className="text-slate-500 dark:text-slate-400">Environment: </span>
            {config.appEnv}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API connection</CardTitle>
          <CardDescription>Live status of the worker this dashboard talks to.</CardDescription>
        </CardHeader>
        <CardContent>
          {health.isPending ? <LoadingState label="Checking API" /> : null}
          {health.isError ? (
            <ErrorState
              title="Cannot reach the API"
              description="The worker is not responding. Start it with `pnpm dev:worker`."
              onRetry={() => void health.refetch()}
            />
          ) : null}
          {health.data ? (
            <div className="flex items-center gap-3 text-sm">
              <Badge tone="success">Connected</Badge>
              <span className="text-slate-600 dark:text-slate-400">
                Environment {health.data.appEnv}
                {health.data.simulatorEnabled ? ' · simulator active' : ''}
              </span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <ShopifyConnection />

      <Card>
        <CardHeader>
          <CardTitle>Other providers</CardTitle>
          <CardDescription>
            Inbound email, Brevo and the AI provider are configured by environment variable in this
            build.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            They have no OAuth flow — each is an API key set as a worker secret. A connection UI
            appears here only once it is backed by real credential storage; a button that cannot
            connect is worse than no button.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  // The connection card reads and mutates organisation credentials, so this
  // page now requires a session rather than being a public status readout.
  component: () => (
    <RequireSession>
      <SettingsPage />
    </RequireSession>
  ),
});
